import clsx from "clsx"

import type { WorkItem } from "@/types/sprint-report"

interface Props {
  items: WorkItem[]
}

const PRIORITY_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 }
const MINOR_RANK: Record<string, number> = { A: 0, B: 1 }

/** Group + order key for an item's sprint: current → next → future → backlog → previous → past. */
function sprintGroup(it: WorkItem): { key: string; label: string; rank: [number, number] } {
  const s = it.sprint
  if (!s) return { key: "backlog", label: "Backlog", rank: [3, 0] }
  const n = s.number ?? 0
  if (s.is_current) return { key: `s${n}`, label: `Sprint ${n} (current)`, rank: [0, 0] }
  if (s.is_next) return { key: `s${n}`, label: `Sprint ${n} (next)`, rank: [1, 0] }
  if (s.is_future) return { key: `s${n}`, label: `Sprint ${n}`, rank: [2, n] }
  if (s.is_previous) return { key: `s${n}`, label: `Sprint ${n} (previous)`, rank: [4, -n] }
  return { key: `s${n}`, label: `Sprint ${n}`, rank: [5, -n] }
}

function priorityRank(it: WorkItem): [number, number, number] {
  return [
    PRIORITY_RANK[it.priority.major] ?? 9,
    MINOR_RANK[it.priority.minor ?? ""] ?? 2,
    it.state_order,
  ]
}

export function WorkedOn({ items }: Props) {
  const open = items.filter((it) => it.state_category !== "done")

  const groups = new Map<string, { label: string; rank: [number, number]; rows: WorkItem[] }>()
  for (const it of open) {
    const g = sprintGroup(it)
    const entry = groups.get(g.key) ?? { label: g.label, rank: g.rank, rows: [] }
    entry.rows.push(it)
    groups.set(g.key, entry)
  }
  const ordered = [...groups.values()].sort(
    (a, b) => a.rank[0] - b.rank[0] || a.rank[1] - b.rank[1],
  )
  for (const g of ordered) {
    g.rows.sort((a, b) => {
      const ra = priorityRank(a)
      const rb = priorityRank(b)
      return ra[0] - rb[0] || ra[1] - rb[1] || ra[2] - rb[2]
    })
  }

  if (open.length === 0) {
    return (
      <p className="rounded border border-slate-300 p-6 text-center text-sm text-slate-500">
        No open worked-on items (R1).
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-[11px] text-slate-500">
        Open R1 items by sprint. <strong>Status</strong> is the item's own state;{" "}
        <strong>Bundle status</strong> is the parent bundle's state — shown in{" "}
        <span className="font-semibold text-orange-600">orange</span> when it differs.
      </p>
      {ordered.map((g) => (
        <section key={g.label}>
          <h2 className="mb-1 text-sm font-semibold text-header">
            {g.label} <span className="font-normal text-slate-400">({g.rows.length})</span>
          </h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200/80">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pri</th>
                  <th>Status</th>
                  <th>Bundle status</th>
                  <th>Assignee</th>
                  <th>Title</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((it) => (
                  <WorkedOnRow key={it.id} it={it} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  )
}

function WorkedOnRow({ it }: { it: WorkItem }) {
  const b = it.bundle
  const mismatch = Boolean(b?.state && b.state !== it.state)
  return (
    <tr className="border-t border-slate-200 align-top hover:bg-slate-50">
      <td className="whitespace-nowrap px-2 py-1">
        <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
          {it.id}
        </a>
      </td>
      <td className="whitespace-nowrap px-2 py-1 font-medium">{it.priority.raw}</td>
      <td className="whitespace-nowrap px-2 py-1">{it.state}</td>
      <td className="whitespace-nowrap px-2 py-1">
        {b ? (
          <>
            <a
              href={b.url}
              target="_blank"
              rel="noreferrer"
              className="text-slate-500 hover:underline"
            >
              {b.code ?? b.title}
            </a>
            {" · "}
            <span className={clsx(mismatch ? "font-semibold text-orange-600" : "text-slate-600")}>
              {b.state}
            </span>
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td
        className={clsx(
          "whitespace-nowrap px-2 py-1",
          it.assigned_to ? "text-slate-600" : "italic text-slate-400",
        )}
      >
        {it.assigned_to?.name ?? "Unassigned"}
      </td>
      <td className="px-2 py-1">
        <a href={it.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:underline">
          {it.title}
        </a>
      </td>
    </tr>
  )
}
